type Listener<T> = (val: T) => void;
type Unsubscriber = () => void;

interface IObservableOptions<T> {
  /**
   * What the server renders and what the client renders while hydrating, for
   * an observable whose initial value comes from browser storage. Without it
   * a returning player's first client render would disagree with the HTML the
   * server produced for an anonymous visitor; with it, both render this and
   * React applies the stored value in the re-render that follows hydration.
   */
  serverSnapshot: T;
}

// Based on https://blog.betomorrow.com/replacing-redux-with-observables-and-react-hooks-acdbbaf5ba80
export class Observable<T> {
  private _listeners: Listener<T>[] = [];
  private _value: T | undefined;
  private readonly _serverSnapshot: { value: T } | null;

  constructor(initialValue?: T, options?: IObservableOptions<T>) {
    this._value = initialValue;
    this._serverSnapshot = options ? { value: options.serverSnapshot } : null;
  }

  get = (): T | undefined => this._value;

  /** The value to render on the server and during hydration; the current value unless one was declared. */
  getServerSnapshot = (): T | undefined =>
    this._serverSnapshot ? this._serverSnapshot.value : this._value;

  set = (value: T): void => {
    if (this._value !== value) {
      this._value = value;
      this._listeners.forEach((listener) => listener(value));
    }
  };

  subscribe = (listener: Listener<T>): Unsubscriber => {
    this._listeners.push(listener);

    return () => {
      this._listeners = this._listeners.filter((_listener) => _listener !== listener);
    };
  };
}
